/**
 * ACADS — freezing a month (HQ view).
 *
 * A month is the month a payment was received in. Freezing it locks every
 * payment dated in it: the centre can no longer edit or cancel one, or date a
 * new payment into it. A fee that comes in late is collected as usual and
 * belongs to the month it was received.
 *
 * The centre cannot unfreeze. It asks, with a reason, from Daily receipts; the
 * request shows on top here and HQ either unfreezes the month or declines.
 */
import {
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import { fetchAcadsFreeze, setAcadsFreeze } from 'api'
import constants from 'app/constants'
import { showToast } from 'app/helpers'

const useStyles = makeStyles((theme) => ({
  rule: {
    background: '#f4f6f8',
    border: '1px solid #e4e7ec',
    borderRadius: 6,
    padding: '10px 14px',
    marginBottom: theme.spacing(2)
  },
  ask: {
    background: '#fff6dc',
    color: '#7a5200',
    borderRadius: 6,
    padding: '10px 14px',
    marginBottom: theme.spacing(1.5),
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  reason: { whiteSpace: 'pre-line' },
  actions: { display: 'flex', gap: theme.spacing(1) },
  table: {
    borderCollapse: 'collapse',
    width: '100%',
    border: '1px solid #e4e7ec',
    '& th, & td': {
      borderBottom: '1px solid #ececec',
      padding: '10px 12px',
      textAlign: 'left'
    },
    '& th': {
      background: '#f4f6f8',
      fontSize: 11,
      textTransform: 'uppercase',
      letterSpacing: '0.03em',
      color: '#555'
    }
  },
  num: { textAlign: 'right !important', fontVariantNumeric: 'tabular-nums' },
  frozen: { background: '#e8eef8' },
  pillFrozen: { background: '#d5e2f6', color: '#2b5fa8', fontWeight: 600 },
  pillOpen: { background: '#e3f3ea', color: '#1c6b43', fontWeight: 600 },
  sub: { display: 'block', fontSize: 12, color: theme.palette.text.secondary }
}))

const inr = (v) =>
  `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`

const AcadsFreeze = ({ filterSlot }) => {
  const classes = useStyles()
  const [data, setData] = useState(null)
  const [failed, setFailed] = useState(false)
  const [busy, setBusy] = useState(false)
  const [year, setYear] = useState('')

  const load = useCallback(
    () =>
      fetchAcadsFreeze(year)
        .then(setData)
        .catch(() => setFailed(true)),
    [year]
  )
  useEffect(() => {
    load()
  }, [load])

  const act = async (month, action, done) => {
    setBusy(true)
    try {
      setData(await setAcadsFreeze({ month: month.month, action, year }))
      showToast(`${month.label} ${done}.`, constants.SUCCESS)
    } catch (err) {
      showToast(
        (err.response && err.response.data && err.response.data.detail) ||
          'Could not change the month',
        constants.ERROR
      )
    } finally {
      setBusy(false)
    }
  }

  if (!data) {
    return failed ? (
      <Typography color="error">
        Could not load the months. Refresh the page.
      </Typography>
    ) : (
      <CircularProgress size={24} />
    )
  }

  const asked = data.months.filter((m) => m.request)
  const yearPicker = (data.years || []).length > 1 && (
    <TextField
      select
      size="small"
      variant="outlined"
      label="Financial year"
      style={{ minWidth: 150 }}
      value={data.year}
      onChange={(e) => setYear(e.target.value)}
    >
      {data.years.map((y) => (
        <MenuItem key={y.year} value={y.year}>
          {y.label}
        </MenuItem>
      ))}
    </TextField>
  )

  return (
    <div>
      <div className={classes.rule}>
        Freezing a month locks every payment <b>dated in that month</b>: the
        centre cannot edit or cancel it. A fee that comes in late is collected
        as usual and sits in the month it was received.
        {data.can_freeze
          ? ''
          : ' You can see the months here; only HQ Finance can freeze or unfreeze.'}
      </div>

      {yearPicker &&
        (filterSlot ? (
          createPortal(yearPicker, filterSlot)
        ) : (
          <div style={{ marginBottom: 16 }}>{yearPicker}</div>
        ))}

      {asked.map((m) => (
        <div key={m.month} className={classes.ask}>
          <span>
            <b>Asked to reopen {m.label}</b>
            {m.request.at ? ` on ${dayjs(m.request.at).format('D MMM')}` : ''}:
            <span className={classes.reason}> {m.request.reason}</span>
          </span>
          {data.can_freeze && (
            <span className={classes.actions}>
              <Button
                size="small"
                variant="contained"
                color="primary"
                disabled={busy}
                onClick={() => act(m, 'unfreeze', 'is open again')}
              >
                Unfreeze {m.label.split(' ')[0]}
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={busy}
                onClick={() => act(m, 'decline', 'stays frozen')}
              >
                Decline
              </Button>
            </span>
          )}
        </div>
      ))}

      <table className={classes.table}>
        <thead>
          <tr>
            <th>Month{data.year_label ? ` (${data.year_label})` : ''}</th>
            <th className={classes.num}>Receipts</th>
            <th className={classes.num}>Collected</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {data.months.map((m) => (
            <tr key={m.month} className={m.frozen ? classes.frozen : ''}>
              <td>
                <b>{m.label}</b>
                {!m.frozen && m.no_proof > 0 && (
                  <span className={classes.sub}>
                    Check before freezing: {m.no_proof} non-cash payment
                    {m.no_proof > 1 ? 's have' : ' has'} no receipt
                  </span>
                )}
              </td>
              <td className={classes.num}>{m.receipts}</td>
              <td className={classes.num}>{inr(m.collected)}</td>
              <td>
                {m.frozen ? (
                  <>
                    <Chip
                      size="small"
                      className={classes.pillFrozen}
                      label="Frozen"
                    />
                    <span className={classes.sub}>
                      by {m.frozen_by || 'HQ'}
                      {m.frozen_at
                        ? ` on ${dayjs(m.frozen_at).format('D MMM YYYY')}`
                        : ''}
                    </span>
                  </>
                ) : (
                  <Chip
                    size="small"
                    className={classes.pillOpen}
                    label="Open"
                  />
                )}
              </td>
              <td>
                {data.can_freeze &&
                  (m.frozen ? (
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={busy}
                      onClick={() => act(m, 'unfreeze', 'is open again')}
                    >
                      Unfreeze
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      disabled={busy}
                      onClick={() => act(m, 'freeze', 'is frozen')}
                    >
                      Freeze
                    </Button>
                  ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default AcadsFreeze
